/**
 * Project hooks (Phase 2 minimal + Phase 5 UI + Phase 2.1 Active Dataset).
 *
 * - useProjects: list all projects for current account
 * - useProject:   GET /projects/:id (Phase 2.1 — нужен для sync active_dataset_id)
 * - useCreateProject: POST /projects
 * - useRenameProject: PATCH /projects/:id
 * - useDeleteProject: DELETE /projects/:id
 * - useSetActiveDataset: PATCH /projects/:id/active-dataset (Phase 2.1)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '@/lib/api';

export interface Project {
  id: string;
  name: string;
  is_archived: boolean;
  /**
   * Phase 2.1 (Files UX Refactor 2026-08-06):
   * ID активного dataset'а проекта на бэкенде (project.active_dataset_id).
   * null = нет активного. После F5 / смены проекта — нужно sync через
   * `useProject` → `setActiveDataset(id)`.
   */
  active_dataset_id: string | null;
}

export interface ProjectListResponse {
  items: Project[];
  total: number;
}

export function useProjects() {
  return useQuery<ProjectListResponse>({
    queryKey: ['projects'],
    queryFn: async () => (await api.get<ProjectListResponse>('/projects')).data,
  });
}

export function useProject(projectId: string | null) {
  /**
   * GET /projects/{id} — Phase 2.1 используется для первичной синхронизации
   * active_dataset_id с сервером. enabled-guard защищает от запросов с null.
   */
  return useQuery<Project>({
    queryKey: ['projects', projectId],
    queryFn: async () =>
      (await api.get<Project>(`/projects/${projectId}`)).data,
    enabled: Boolean(projectId),
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) =>
      (await api.post<Project>('/projects', { name })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useRenameProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) =>
      (await api.patch<Project>(`/projects/${id}`, { name })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/projects/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * Phase 2.1: PATCH /projects/{id}/active-dataset — назначить активный dataset.
 *
 * Использование:
 *   const setActive = useSetActiveDataset();
 *   setActive.mutate({ projectId, datasetId: ds.id });      // назначить
 *   setActive.mutate({ projectId, datasetId: null });        // сбросить
 *
 * onSuccess сам инвалидирует ['projects'] и ['projects', id] чтобы
 * и list, и single fetch переотразили изменение. Синхронизация
 * `useProjectStore.activeDatasetId` — ответственность вызывающего
 * компонента (через `setActiveDataset(...)`), потому что mutation может
 * вызываться из разных мест и идтипотентный side-effect на store
 * удобнее держать в одном call-site.
 */
export function useSetActiveDataset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      datasetId,
    }: {
      projectId: string;
      datasetId: string | null;
    }) =>
      (
        await api.patch<Project>(
          `/projects/${projectId}/active-dataset`,
          { active_dataset_id: datasetId },
        )
      ).data,
    onSuccess: (project) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['projects', project.id] });
    },
  });
}
